import React, { useEffect, useRef, useState } from "react";
import { BsThreeDotsVertical } from "react-icons/bs";
import { Dropdown, Tag, Switch, message } from "antd"; // Added message for feedback
import GenericTable from "../../../../Components/Tables/GenericTable";
import {
  PlusCircleOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import GenericModal from "../../../../Components/Modals/GenericModal";
import {
  getVehicleDocumentRequirements,
  deleteVehicleDocumentRequirement,
} from "../../../../API/Admin/SystemConfiguration/VehicleDocuments";
import usePageAccess from "../../../../Utlis/usePageAccess";

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  if (data.detail) return data.detail;

  const fieldErrors = Object.values(data)
    .flat()
    .filter((msg) => typeof msg === "string");

  return fieldErrors.length ? fieldErrors.join(" ") : fallback;
};

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

const mapDocumentRequirement = (item, index) => ({
  key: String(item.id ?? index),
  id: item.id,
  documentName: item.document_name || "-",
  category: capitalize(item.category) || "-",
  expiry: item.has_expiry ? "Yes" : "No",
  remindDays: item.remind_days_before_expiry ?? "-",
  formats: Array.isArray(item.allowed_formats) ? item.allowed_formats : [],
  required: !!item.is_required,
  status: item.status === "active" ? "Active" : "Inactive",
});

const VehicleDocumentRequired = ({ setBtnConfigs }) => {
  const { canEdit } = usePageAccess("system-configuration");
  const navigate = useNavigate();
  const location = useLocation();
  const processedNavKey = useRef(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const [dataSource, setDataSource] = useState([]);

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const res = await getVehicleDocumentRequirements();
        const list = res?.data?.data;

        if (Math.floor(res?.status / 100) === 2 && Array.isArray(list)) {
          setDataSource(list.map(mapDocumentRequirement));
        } else {
          message.error(
            getErrorMessage(res?.data, "Failed to load document requirements."),
          );
        }
      } catch (err) {
        message.error(
          getErrorMessage(
            err?.response?.data,
            "Failed to load document requirements.",
          ),
        );
      }
    };

    fetchDocuments();
  }, []);

  const handleAdd = () => {
    navigate("/add-vehicle-document", {
      state: { from: "Vehicle Document Required", dataSource },
    });
  };

  useEffect(() => {
    if (!location.state?.updatedList) {
      return;
    }
    if (processedNavKey.current === location.key) {
      return;
    }
    processedNavKey.current = location.key;

    setDataSource(location.state.updatedList);

    navigate(location.pathname, {
      replace: true,
      state: { activeTab: "Vehicle Document Required" },
    });
  }, [location, navigate]);

  useEffect(() => {
    setBtnConfigs([
      {
        text: "Add New Document",
        icon: <PlusCircleOutlined />,
        onClick: handleAdd,
      },
    ]);
  }, [setBtnConfigs]);

  const handleEdit = (record) => {
    navigate("/edit-vehicle-document", { state: { id: record.id } });
  };

  const handleDeleteConfirm = async () => {
    if (!selectedRow) return;

    try {
      const res = await deleteVehicleDocumentRequirement(selectedRow.id);

      if (Math.floor(res?.status / 100) === 2) {
        setDataSource((prev) =>
          prev.filter((item) => item.key !== selectedRow.key),
        );
        setIsConfirmOpen(false);
        setSelectedRow(null);
        message.success(
          getErrorMessage(res?.data, "Document deleted successfully"),
        );
      } else {
        message.error(getErrorMessage(res?.data, "Failed to delete document."));
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to delete document."),
      );
    }
  };

  const getUniqueFilters = (data, key) => {
    const uniqueValues = [
      ...new Set(
        data.flatMap((item) =>
          Array.isArray(item[key]) ? item[key] : [item[key]],
        ),
      ),
    ];

    return uniqueValues
      .filter((v) => v && v !== "-")
      .sort()
      .map((val) => ({
        text: val,
        value: val,
      }));
  };

  const categoryFilters = getUniqueFilters(dataSource, "category");
  const expiryFilters = getUniqueFilters(dataSource, "expiry");
  const formatFilters = getUniqueFilters(dataSource, "formats");
  const statusFilters = getUniqueFilters(dataSource, "status");

  const columns = [
    {
      title: "Document Name",
      dataIndex: "documentName",
      key: "documentName",
      enableSearch: true,
      render: (text, record) => (
        <span
          style={{
            fontWeight: 600,
            color: record.status === "Inactive" ? "#999" : "inherit",
          }}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      filters: categoryFilters,
      onFilter: (value, record) => record.category === value,
      render: (category, record) => {
        const colors = {
          Legal: { bg: "#EEF2FF", text: "#6366F1" },
          Financial: { bg: "#F0FDF4", text: "#22C55E" },
          Compliance: { bg: "#FFFBEB", text: "#F59E0B" },
          Operational: { bg: "#FFF1F2", text: "#F43F5E" },
          General: { bg: "#F8FAFC", text: "#64748B" },
        };
        const style = colors[category] || colors.General;
        return (
          <span
            style={{
              backgroundColor: style.bg,
              color: style.text,
              padding: "4px 12px",
              borderRadius: "8px",
              fontSize: "12px",
              fontWeight: "500",
              // Apply opacity if inactive
              opacity: record.status === "Inactive" ? 0.5 : 1,
              filter: record.status === "Inactive" ? "grayscale(1)" : "none",
            }}
          >
            {category}
          </span>
        );
      },
    },

    {
      title: "Expiry",
      dataIndex: "expiry",
      key: "expiry",
      filters: expiryFilters,
      onFilter: (value, record) => record.expiry === value,
      render: (expiry, record) => (
        <span
          style={{
            color: expiry === "Yes" ? "#EF4444" : "#A1A1AA",
            fontWeight: 500,
            opacity: record.status === "Inactive" ? 0.5 : 1,
          }}
        >
          {expiry}
        </span>
      ),
    },
    {
      title: "Formats",
      dataIndex: "formats",
      key: "formats",
      filters: formatFilters,
      onFilter: (value, record) => record.formats?.includes(value),
      render: (formats, record) => (
        <div
          style={{
            display: "flex",
            gap: "6px",
            flexWrap: "wrap",
            opacity: record.status === "Inactive" ? 0.5 : 1,
          }}
        >
          {Array.isArray(formats) && formats.length
            ? formats.map((format, index) => (
                <span key={index} className="assign-module-badge">
                  {format}
                </span>
              ))
            : "-"}
        </div>
      ),
    },
    {
      title: "Required",
      dataIndex: "required",
      key: "required",
      render: (val, record) => (
        <div style={{ opacity: record.status === "Inactive" ? 0.5 : 1 }}>
          <Switch
            checked={val}
            size="small"
            disabled={record.status === "Inactive"}
          />
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const items = [
          {
            key: "edit",
            label: "Edit",
            style: { color: "#4F46E5", fontWeight: 500 },
            icon: <EditOutlined />,
            onClick: () => handleEdit(record),
          },
          {
            key: "delete",
            label: "Delete",
            style: { color: " #DC2626", fontWeight: 500 },
            icon: <DeleteOutlined />,
            onClick: () => {
              setSelectedRow(record);
              setIsConfirmOpen(true);
            },
          },
        ];
        return (
          <div style={{ opacity: record.status === "Inactive" ? 0.6 : 1 }}>
            <Dropdown
              menu={{ items }}
              trigger={["click"]}
              placement="bottomRight"
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  width: 24,
                  height: 24,
                }}
              >
                <BsThreeDotsVertical
                  style={{ fontSize: 20, color: "#4F46E5" }}
                />
              </span>
            </Dropdown>
          </div>
        );
      },
    },
  ];

  return (
    <div className="document-management-table">
      <GenericTable
        dataSource={dataSource}
        columns={columns.filter((c) => canEdit || c.key !== "actions")}
      />

      <GenericModal
        open={isConfirmOpen}
        title="Delete Document"
        subtitle={`Are you sure you want to delete ${selectedRow?.documentName} ?`}
        highlightText={selectedRow?.documentName}
        showReason={false}
        confirmText="Delete"
        cancelText="Cancel"
        iconColor="#ef4444"
        iconBgColor="#ef44441a"
        confirmBtnColor="#EA580C"
        onCancel={() => setIsConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};

export default VehicleDocumentRequired;
